import React from 'react';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import moment from 'moment';
import FormattedCurrency from '@/shared/components/FormattedCurrency';
import RelationName from '@/shared/components/RelationName';
import NavigateToState from '@/shared/components/NavigateToState';
import { mapInvoiceStreamName } from '@/shared/services/ObjectDisplayService';
import messages from '../reportingMessages';

const HoursColumn = ({ value }) => (
  <span className={classnames('text-right hours', { 'text-muted': !value })}>
    {(value || 0).toTime()}
  </span>
);

const AmountColumn = ({ value }) => (
  <span className={classnames('text-right amount', { 'text-muted': !value })}>
    <FormattedCurrency value={value || 0} />
  </span>
);

const HoursColumnWithNavigation = ({ value, stateName, navigationProps }) => (
  <span className={classnames('text-right hours', { 'text-muted': !value })}>
    <NavigateToState stateName={stateName} useUrl paramsToJson {...navigationProps}>
      {(value || 0).toTime()}
    </NavigateToState>
  </span>
);

const AmountColumnWithNavigation = ({ value, stateName, navigationProps }) => (
  <span className={classnames('text-right amount', { 'text-muted': !value })}>
    <NavigateToState stateName={stateName} useUrl paramsToJson {...navigationProps}>
      <FormattedCurrency value={value || 0} />
    </NavigateToState>
  </span>
);

const buildRelation = row => {
  const now = moment.utc();
  return {
    id: row.relationId,
    type: row.relationType,
    name: row.relationDisplayName,
    alias: row.relationAlias,
    isActive:
      !row.relationEndDate || (row.relationEndDate && moment.utc(row.relationEndDate).isAfter(now)),
    relationHandlingLevel: row.relationHandlingLevel,
    relationHandlingLevelClarification: row.relationHandlingLevelClarification,
  };
};

const getDateParams = reportFilters => {
  const dateFrom = reportFilters.dateFrom ? moment(reportFilters.dateFrom).toDate() : null;
  const dateUntil = reportFilters.dateUntil ? moment(reportFilters.dateUntil).toDate() : null;
  return { dateFrom, dateUntil };
};

const getRegisteredHoursNavProps = (row, reportFilters) => {
  const relation = buildRelation(row);
  const { dateFrom, dateUntil } = getDateParams(reportFilters);
  return {
    stateParams: {
      relationId: row.relationId,
      relation,
      finalInvoiceStreamId: row.invoiceStreamId,
      period: 'all',
      invoiceDateFrom: dateFrom,
      invoiceDateUntil: dateUntil,
      filters: {
        type: { items: [0] },
      },
    },
  };
};

const getInForfaitHoursNavProps = (row, reportFilters) => {
  const relation = buildRelation(row);
  const { dateFrom, dateUntil } = getDateParams(reportFilters);
  return {
    stateParams: {
      relationId: row.relationId,
      relation,
      finalInvoiceStreamId: row.invoiceStreamId,
      period: 'all',
      invoiceDateFrom: dateFrom,
      invoiceDateUntil: dateUntil,
      filters: {
        type: { items: [0] },
        forfaitStatus: { items: [2] },
      },
    },
  };
};

const getRegisteredCostsNavProps = (row, reportFilters) => {
  const relation = buildRelation(row);
  const { dateFrom, dateUntil } = getDateParams(reportFilters);
  return {
    stateParams: {
      relationId: row.relationId,
      relation,
      finalInvoiceStreamId: row.invoiceStreamId,
      period: 'all',
      invoiceDateFrom: dateFrom,
      invoiceDateUntil: dateUntil,
      filters: {
        type: { items: [1, 2] },
      },
    },
  };
};

const getWrittenOffNavProps = (row, reportFilters) => {
  const relation = buildRelation(row);
  const { dateFrom, dateUntil } = getDateParams(reportFilters);
  return {
    stateParams: {
      relationId: row.relationId,
      relation,
      invoiceStreamId: row.invoiceStreamId,
      dateFrom,
      dateUntil,
      invoiceDateFrom: null,
      invoiceDateUntil: null,
      showWrittenOffInternally: 1,
    },
    functionality: 'InvoiceFinal',
    permission: 'Read',
  };
};

const getTurnoverNavProps = (row, reportFilters) => {
  const relation = buildRelation(row);
  const { dateFrom, dateUntil } = getDateParams(reportFilters);
  return {
    stateParams: {
      relationId: row.relationId,
      relation,
      invoiceStreamId: row.invoiceStreamId,
      dateFrom,
      dateUntil,
      invoiceDateFrom: null,
      invoiceDateUntil: null,
    },
    functionality: 'InvoiceFinal',
    permission: 'Read',
  };
};

const getOpenRegisteredHoursNavProps = row => {
  const relation = buildRelation(row);
  return {
    stateParams: {
      relationId: row.relationId,
      relation,
      finalInvoiceStreamId: row.invoiceStreamId,
      period: 'all',
      filters: {
        invoiceStatus: { items: [0, 1] },
        type: { items: [0] },
      },
    },
  };
};

const getOpenRegisteredCostsNavProps = row => {
  const relation = buildRelation(row);
  return {
    stateParams: {
      relationId: row.relationId,
      relation,
      finalInvoiceStreamId: row.invoiceStreamId,
      period: 'all',
      filters: {
        invoiceStatus: { items: [0, 1] },
        type: { items: [1, 2] },
      },
    },
  };
};

const getNeverInvoiceHoursNavProps = row => {
  const relation = buildRelation(row);
  return {
    stateParams: {
      relationId: row.relationId,
      relation,
      finalInvoiceStreamId: row.invoiceStreamId,
      period: 'all',
      filters: {
        invoiceStatus: { items: [-1] },
        type: { items: [0] },
      },
    },
  };
};

const getNeverInvoiceCostsNavProps = row => {
  const relation = buildRelation(row);
  return {
    stateParams: {
      relationId: row.relationId,
      relation,
      finalInvoiceStreamId: row.invoiceStreamId,
      period: 'all',
      filters: {
        invoiceStatus: { items: [-1] },
        type: { items: [1, 2] },
      },
    },
  };
};

export const getColumns = (
  intl,
  columnStatus,
  showAssignments,
  showHours,
  totals,
  reportFilters,
  sectors,
  invoicingtypes,
) => {
  const columns = [
    {
      Header: <FormattedMessage {...messages.Relation} />,
      Footer: <FormattedMessage {...messages.Totals} />,
      id: 'relation',
      accessor: originalRow => originalRow,
      Cell: ({ row: { original } }) => {
        const relation = buildRelation(original);
        return <RelationName relation={relation} />;
      },
      isVisible: true,
      width: 2,
      minWidth: 100,
    },
    {
      Header: <FormattedMessage {...messages.Assignment} />,
      id: 'assignment',
      accessor: originalRow => mapInvoiceStreamName(originalRow, intl, ''),
      isVisible: showAssignments,
    },
    {
      Header: <FormattedMessage {...messages.Sector} />,
      id: 'sector',
      accessor: originalRow =>
        !originalRow.sector && originalRow.sector !== 0
          ? intl.formatMessage(messages.None)
          : (sectors && sectors[originalRow.sector]) || '',
      isVisible: columnStatus.sector,
    },
    {
      Header: <FormattedMessage {...messages.InvoicingType} />,
      id: 'invoicingType',
      accessor: originalRow =>
        !originalRow.invoicingType && originalRow.invoicingType !== 0
          ? intl.formatMessage(messages.None)
          : (invoicingtypes && invoicingtypes[originalRow.invoicingType]) || '',
      Cell: ({ row: { original }, value }) => (
        <span title={original.invoicingScheduleName}>{value}</span>
      ),
      isVisible: columnStatus.invoicingType,
    },
    {
      Header: <FormattedMessage {...messages.RegisteredHours} />,
      id: 'registeredHours',
      Cell: ({ row: { original } }) => {
        const navProps = getRegisteredHoursNavProps(original, reportFilters);
        return showHours ? (
          <HoursColumnWithNavigation
            value={original.registeredHours}
            stateName="registrations.overview"
            navigationProps={navProps}
          />
        ) : (
          <AmountColumnWithNavigation
            value={original.registeredHoursAmount}
            stateName="registrations.overview"
            navigationProps={navProps}
          />
        );
      },
      Footer: () =>
        showHours
          ? HoursColumn({ value: totals.registeredHours })
          : AmountColumn({ value: totals.registeredHoursAmount }),
      isVisible: columnStatus.registeredHours,
      align: 'right',
    },
    {
      Header: <FormattedMessage {...messages.BilledHours} />,
      id: 'billedHours',
      Cell: ({ row: { original } }) =>
        showHours
          ? HoursColumn({ value: original.billedHours })
          : AmountColumn({ value: original.billedHoursAmount }),
      Footer: () =>
        showHours
          ? HoursColumn({ value: totals.billedHours })
          : AmountColumn({ value: totals.billedHoursAmount }),
      isVisible: columnStatus.billedHours,
      align: 'right',
    },
    {
      Header: <FormattedMessage {...messages.InForfait} />,
      id: 'inForfaitHours',
      Cell: ({ row: { original } }) => {
        const navProps = getInForfaitHoursNavProps(original, reportFilters);
        return showHours ? (
          <HoursColumnWithNavigation
            value={original.inForfaitHours}
            stateName="registrations.overview"
            navigationProps={navProps}
          />
        ) : (
          <AmountColumnWithNavigation
            value={original.inForfaitHoursAmount}
            stateName="registrations.overview"
            navigationProps={navProps}
          />
        );
      },
      Footer: () =>
        showHours
          ? HoursColumn({ value: totals.inForfaitHours })
          : AmountColumn({ value: totals.inForfaitHoursAmount }),
      isVisible: columnStatus.inForfaitHours,
      align: 'right',
    },
    {
      Header: <FormattedMessage {...messages.AddedHours} />,
      id: 'addedHours',
      Cell: ({ row: { original } }) =>
        showHours
          ? HoursColumn({ value: original.addedHours })
          : AmountColumn({ value: original.addedHoursAmount }),
      Footer: () =>
        showHours
          ? HoursColumn({ value: totals.addedHours })
          : AmountColumn({ value: totals.addedHoursAmount }),
      isVisible: columnStatus.addedHours,
      align: 'right',
    },
    {
      Header: <FormattedMessage {...messages.RemovedHours} />,
      id: 'removedHours',
      Cell: ({ row: { original } }) =>
        showHours
          ? HoursColumn({ value: original.removedHours })
          : AmountColumn({ value: original.removedHoursAmount }),
      Footer: () =>
        showHours
          ? HoursColumn({ value: totals.removedHours })
          : AmountColumn({ value: totals.removedHoursAmount }),
      isVisible: columnStatus.removedHours,
      align: 'right',
    },
    {
      Header: <FormattedMessage {...messages.RegisteredCosts} />,
      id: 'registeredCosts',
      Cell: ({ row: { original } }) => {
        const navProps = getRegisteredCostsNavProps(original, reportFilters);
        return (
          <AmountColumnWithNavigation
            value={original.registeredCosts}
            stateName="registrations.overview"
            navigationProps={navProps}
          />
        );
      },
      Footer: () => AmountColumn({ value: totals.registeredCosts }),
      isVisible: columnStatus.registeredCosts,
      align: 'right',
    },
    {
      Header: <FormattedMessage {...messages.BilledCosts} />,
      id: 'billedCosts',
      accessor: 'billedCosts',
      Cell: ({ value }) => AmountColumn({ value }),
      Footer: () => AmountColumn({ value: totals.billedCosts }),
      isVisible: columnStatus.billedCosts,
      align: 'right',
    },
    {
      Header: <FormattedMessage {...messages.FixedAmountOrAdvance} />,
      id: 'fixedAmounts',
      accessor: 'fixedAmounts',
      Cell: ({ value }) => AmountColumn({ value }),
      Footer: () => AmountColumn({ value: totals.fixedAmounts }),
      isVisible: columnStatus.fixedAmounts,
      align: 'right',
    },
    {
      Header: <FormattedMessage {...messages.FreeLines} />,
      id: 'freeLines',
      accessor: 'freeLines',
      Cell: ({ value }) => AmountColumn({ value }),
      Footer: () => AmountColumn({ value: totals.freeLines }),
      isVisible: columnStatus.freeLines,
      align: 'right',
    },
    {
      Header: <FormattedMessage {...messages.WrittenOffAmount} />,
      id: 'writtenOffAmount',
      Cell: ({ row: { original } }) => {
        const navProps = getWrittenOffNavProps(original, reportFilters);
        return (
          <AmountColumnWithNavigation
            value={original.writtenOffAmount}
            stateName="invoicing.overview"
            navigationProps={navProps}
          />
        );
      },
      Footer: () => AmountColumn({ value: totals.writtenOffAmount }),
      isVisible: columnStatus.writtenOffAmount,
      align: 'right',
    },
    {
      Header: <FormattedMessage {...messages.Turnover} />,
      id: 'turnover',
      Cell: ({ row: { original } }) => {
        const navProps = getTurnoverNavProps(original, reportFilters);
        return (
          <AmountColumnWithNavigation
            value={original.turnover}
            stateName="invoicing.overview"
            navigationProps={navProps}
          />
        );
      },
      Footer: () => AmountColumn({ value: totals.turnover }),
      isVisible: columnStatus.turnover,
      align: 'right',
    },
    {
      Header: <FormattedMessage {...messages.InternalCost} />,
      id: 'internalCost',
      accessor: 'internalCost',
      Cell: ({ value }) => AmountColumn({ value }),
      Footer: () => AmountColumn({ value: totals.internalCost }),
      isVisible: columnStatus.internalCost,
      align: 'right',
    },
    {
      Header: <FormattedMessage {...messages.Profit} />,
      headerTitle: intl.formatMessage(messages.TurnoverMinInternalCost),
      id: 'profit',
      accessor: 'profit',
      Cell: ({ value }) => AmountColumn({ value }),
      Footer: () => AmountColumn({ value: totals.profit }),
      isVisible: columnStatus.profit,
      align: 'right',
    },
    {
      Header: <FormattedMessage {...messages.Rentability} />,
      headerTitle: intl.formatMessage(messages.TurnoverMinRegistered),
      id: 'turnoverMinRegistered',
      accessor: 'turnoverMinRegistered',
      Cell: ({ value }) => AmountColumn({ value }),
      Footer: () => AmountColumn({ value: totals.turnoverMinRegistered }),
      isVisible: columnStatus.turnoverMinRegistered,
      align: 'right',
    },
    {
      Header: <FormattedMessage {...messages.OpenRegisteredHours} />,
      id: 'openRegisteredHours',
      Cell: ({ row: { original } }) => {
        const navProps = getOpenRegisteredHoursNavProps(original);
        return showHours ? (
          <HoursColumnWithNavigation
            value={original.openRegisteredHours}
            stateName="registrations.overview"
            navigationProps={navProps}
          />
        ) : (
          <AmountColumnWithNavigation
            value={original.openRegisteredHoursAmount}
            stateName="registrations.overview"
            navigationProps={navProps}
          />
        );
      },
      Footer: () =>
        showHours
          ? HoursColumn({ value: totals.openRegisteredHours })
          : AmountColumn({ value: totals.openRegisteredHoursAmount }),
      isVisible: columnStatus.openRegisteredHours,
      align: 'right',
    },
    {
      Header: <FormattedMessage {...messages.OpenRegisteredCosts} />,
      id: 'openRegisteredCosts',
      Cell: ({ row: { original } }) => {
        const navProps = getOpenRegisteredCostsNavProps(original);
        return (
          <AmountColumnWithNavigation
            value={original.openRegisteredCosts}
            stateName="registrations.overview"
            navigationProps={navProps}
          />
        );
      },
      Footer: () => AmountColumn({ value: totals.openRegisteredCosts }),
      isVisible: columnStatus.openRegisteredCosts,
      align: 'right',
    },
    {
      Header: <FormattedMessage {...messages.NeverInvoiceRegisteredHours} />,
      id: 'neverInvoiceRegisteredHours',
      Cell: ({ row: { original } }) => {
        const navProps = getNeverInvoiceHoursNavProps(original);
        return showHours ? (
          <HoursColumnWithNavigation
            value={original.neverInvoiceRegisteredHours}
            stateName="registrations.overview"
            navigationProps={navProps}
          />
        ) : (
          <AmountColumnWithNavigation
            value={original.neverInvoiceRegisteredHoursAmount}
            stateName="registrations.overview"
            navigationProps={navProps}
          />
        );
      },
      Footer: () =>
        showHours
          ? HoursColumn({ value: totals.neverInvoiceRegisteredHours })
          : AmountColumn({ value: totals.neverInvoiceRegisteredHoursAmount }),
      isVisible: columnStatus.neverInvoiceRegisteredHours,
      align: 'right',
    },
    {
      Header: <FormattedMessage {...messages.NeverInvoiceRegisteredCosts} />,
      id: 'neverInvoiceRegisteredCosts',
      Cell: ({ row: { original } }) => {
        const navProps = getNeverInvoiceCostsNavProps(original);
        return (
          <AmountColumnWithNavigation
            value={original.neverInvoiceRegisteredCosts}
            stateName="registrations.overview"
            navigationProps={navProps}
          />
        );
      },
      Footer: () => AmountColumn({ value: totals.neverInvoiceRegisteredCosts }),
      isVisible: columnStatus.neverInvoiceRegisteredCosts,
      align: 'right',
    },
  ];

  return columns
    .map(x => {
      if (typeof x.isVisible !== 'undefined' && !x.isVisible) return null;
      return x;
    })
    .filter(Boolean);
};

export const defaultColumn = {
  // When using the useFlexLayout:
  minWidth: 30, // minWidth is only used as a limit for resizing
  width: 1, // width is used for both the flex-basis and flex-grow
  maxWidth: 200, // maxWidth is only used as a limit for resizing
};
